import { z } from "zod";
import { PhoneCallDirectionSchema } from "@remi/enums/phone-call-direction";
import type { PhoneCallStatus } from "@remi/enums/phone-call-status";
import { PhoneCallStatusSchema } from "@remi/enums/phone-call-status";
import type { PreferredLanguage } from "@remi/enums/preferred-language";
import type { SmsDirection } from "@remi/enums/sms-direction";
import type { SmsError } from "@remi/enums/sms-error";
import type { SmsStatus } from "@remi/enums/sms-status";
import type { VoicePrompt } from "@remi/enums/voice-prompt";

import type { MimeType } from "./mimetype";
import { MimeTypeSchema } from "./mimetype";
import type { PhoneData } from "./phoneType";

export const PhoneProviderValues = ["OPEN_PHONE", "TWILIO", "EQHO", "SCHEDULED", "BIRD"] as const;
export const PhoneProviderSchema = z.enum(PhoneProviderValues);
export const PhoneProvider = PhoneProviderSchema.enum;
export type PhoneProvider = z.infer<typeof PhoneProviderSchema>;
export const PhoneProviderDisplayName = {
  [PhoneProvider.OPEN_PHONE]: "OpenPhone",
  [PhoneProvider.TWILIO]: "Twilio",
  [PhoneProvider.EQHO]: "Eqho",
  [PhoneProvider.SCHEDULED]: "Scheduled",
  [PhoneProvider.BIRD]: "Bird",
} as const;

type ListActiveNumbersReturn = {
  id: string;
  number: string;
  name: string;
  users?: { id: string; firstName?: string | null; lastName?: string | null; email?: string | null }[] | null;
}[];

export type ActiveNumbers = ListActiveNumbersReturn;

type ReceiveInput = unknown;

export type ListActiveNumbersFn = () => Promise<ListActiveNumbersReturn>;

type ParsePhoneCallReceivedWebhookInput = ReceiveInput;

// Zod schemas for phone call webhook processing
const PhoneNumberSchema = z.object({
  externalId: z.string().nullable(),
  phoneNumber: z.string(),
});

const ParsePhoneCallReceivedWebhookResultReturnSchema = z.object({
  externalId: z.string(),
  startedAt: z.coerce.date(),
  answeredAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  telecomProviderUserId: z.string().nullable(),
  direction: PhoneCallDirectionSchema,
  status: PhoneCallStatusSchema,
  to: z.array(PhoneNumberSchema),
  from: PhoneNumberSchema,
  recordingFile: z
    .object({
      url: z.string(),
      type: MimeTypeSchema,
    })
    .nullable(),
  webhookBody: z.unknown().nullable(),
  channelId: z.string().optional(),
  /** The provider's own thread id, when it mints one (Quo/OpenPhone does; Twilio does not). */
  providerConversationId: z.string().optional(),
  summary: z.string().optional(),
  nextSteps: z.string().optional(),
});

const ReceiveSkipReturnSchema = z.object({
  skipIngestion: z.literal(true),
  reason: z.string(),
});

export const ParsePhoneCallReceivedWebhookReturnSchema = z.union([
  ReceiveSkipReturnSchema,
  ParsePhoneCallReceivedWebhookResultReturnSchema.extend({
    skipIngestion: z.literal(false).optional(),
  }),
]);

const ReceivePhoneCallRecordingResultReturnSchema = z.object({
  externalId: z.string(),
  recordingFile: z
    .object({
      url: z.string(),
      type: MimeTypeSchema,
    })
    .nullable(),
  recordingExternalId: z.string().optional(),
});

export const ReceivePhoneCallRecordingReturnSchema = z.union([
  ReceiveSkipReturnSchema,
  ReceivePhoneCallRecordingResultReturnSchema.extend({
    skipIngestion: z.literal(false).optional(),
  }),
]);

// Use Zod inference for types (non-breaking change)
export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;
export type ParsePhoneCallReceivedWebhookResultReturn = z.infer<typeof ParsePhoneCallReceivedWebhookResultReturnSchema>;

type ReceivePhoneCallSummaryInput = ReceiveInput;

export type UpsertPhoneCall = Omit<
  ParsePhoneCallReceivedWebhookResultReturn,
  "recordingFile" | "webhookBody" | "from" | "to" | "telecomProviderUserId"
> & {
  provider: PhoneProvider;
  recordingFileId?: string;
  recordingFileName?: string;
  webhookBody: unknown;
  conversationId: string;
  telecomProviderUserIdToConnect?: string;
  telecomProviderPersonId?: string;
  fromPhone: {
    id: string;
    number: string;
    person: {
      id: string;
    } | null;
  };
  toPhones: {
    id: string;
    number: string;
    person: {
      id: string;
    } | null;
  }[];
  fromProxyPhoneId?: string;
  toProxyPhonesIds?: string[];
};

export type UpdatePhoneCall = {
  externalId: string;
  startedAt?: Date;
  answeredAt?: Date | null;
  completedAt?: Date | null;
  status?: PhoneCallStatus;
  recordingFileId?: string | null;
  recordingFileName?: string;
  summary?: string | null;
  nextSteps?: string | null;
};

type ReceivePhoneCallSummaryReturn = UpdatePhoneCall;

export type ReceiveSkipReturn = { skipIngestion: true; reason: string };

// Use Zod inference for the main webhook return type (non-breaking change)
export type ParsePhoneCallReceivedWebhookReturn = z.infer<typeof ParsePhoneCallReceivedWebhookReturnSchema>;

export type ParsePhoneCallReceivedWebhookFn = (
  phoneCall: ParsePhoneCallReceivedWebhookInput,
) => Promise<ParsePhoneCallReceivedWebhookReturn> | ParsePhoneCallReceivedWebhookReturn;

export type ReceivePhoneCallRecordingResultReturn = {
  externalId: string;
  recordingFile: {
    url: string;
    type: MimeType;
  } | null;
  recordingExternalId?: string;
};

export type ReceivePhoneCallRecordingReturn =
  | ReceiveSkipReturn
  | (ReceivePhoneCallRecordingResultReturn & { skipIngestion?: false });

type ReceivePhoneCallRecordingInput = ReceiveInput;

export type ReceivePhoneCallRecordingFn = (
  phoneCall: ReceivePhoneCallRecordingInput,
) => Promise<ReceivePhoneCallRecordingReturn> | ReceivePhoneCallRecordingReturn;

export type ReceivePhoneCallSummaryFn = (
  phoneCall: ReceivePhoneCallSummaryInput,
) => Promise<ReceivePhoneCallSummaryReturn> | ReceivePhoneCallSummaryReturn;

type ReceivePhoneCallTranscriptInput = ReceiveInput;

type ReceivePhoneCallTranscriptReturn = {
  externalId: string;
  dialogue: {
    content: string;
    start: number;
    end: number;
    identifier: string;
    userId: string | null;
  }[];
};

export type ReceivePhoneCallTranscriptFn = (
  phoneCall: ReceivePhoneCallTranscriptInput,
) => Promise<ReceivePhoneCallTranscriptReturn> | ReceivePhoneCallTranscriptReturn;

export type ForwardPhoneCallInput = {
  to: string;
  callerId: string;
  callerPreferredLanguage?: PreferredLanguage;
  calleePreferredLanguage?: PreferredLanguage;
  calleeIsHomeowner?: boolean;
};

export type ForwardPhoneCallReturn = {
  xml?: string;
  json?: {
    action: string;
    to: string;
    from?: string;
    ringTimeout?: number;
    maxDuration?: number;
  };
};

export type ForwardPhoneCallFn = (
  call: ForwardPhoneCallInput,
) => Promise<ForwardPhoneCallReturn> | ForwardPhoneCallReturn;

type SendSmsInput = {
  body?: string;
  media?: Array<{
    url: string;
    contentType?: string;
    filename?: string;
  }>;
  to: string;
  from?: string;
  userId?: string;
  channelId?: string | null;
  includeStatusCallback?: boolean;
  smsMessageId?: string;
  markThreadDone?: boolean;
};

type SendSmsReturn = {
  error?: SmsError;
  externalId?: string;
  status: SmsStatus;
  from: string;
  to: string[];
  provider: PhoneProvider;
  externalPhoneId: string;
  httpStatus?: number;
  providerCode?: string;
};

export type SendSmsFn = (input: SendSmsInput) => Promise<SendSmsReturn>;

type ParseSmsReceivedWebhookInput = ReceiveInput;

export type ParseSmsReceivedWebhookResultReturn = {
  externalId: string;
  status: SmsStatus;
  from: PhoneNumber;
  to: PhoneNumber[];
  body?: string;
  reaction?: {
    emoji: string;
    message: string;
  };
  files?: File[];
  provider: PhoneProvider;
  direction: SmsDirection;
  error?: SmsError;
  userId?: string;
  createdAt?: Date;
  channelId?: string;
  /** The provider's own thread id, when it mints one (Quo/OpenPhone does; Twilio does not). */
  providerConversationId?: string;
};

type ParseSmsReceivedWebhookReturn =
  | ReceiveSkipReturn
  | (ParseSmsReceivedWebhookResultReturn & { skipIngestion?: false });

export type ParseSmsReceivedWebhookFn = (input: ParseSmsReceivedWebhookInput) => Promise<ParseSmsReceivedWebhookReturn>;

type ParseSmsStatusUpdateInput = ReceiveInput;

export type ParseSmsStatusUpdateResult = {
  status: SmsStatus;
  externalId: string;
  provider: PhoneProvider;
  error?: SmsError;
};

type ParseSmsStatusUpdateReturn = { skip: true; reason: string } | ({ skip: false } & ParseSmsStatusUpdateResult);

export type ParseSmsStatusUpdateFn = (
  input: ParseSmsStatusUpdateInput,
) => Promise<ParseSmsStatusUpdateReturn> | ParseSmsStatusUpdateReturn;

type GetSmsStatusReturn = {
  status: SmsStatus | null;
  externalId: string;
  error?: SmsError;
};

export type GetSmsStatusFn = (sid: string) => Promise<GetSmsStatusReturn>;

type GetPhoneNumberInfoInput = {
  phoneNumber: string;
};

type GetPhoneNumberInfoReturn = PhoneData;

export type GetPhoneNumberInfoFn = (input: GetPhoneNumberInfoInput) => Promise<GetPhoneNumberInfoReturn>;

type PurchasePhoneNumberInput = {
  areaCode?: number;
};

type PurchasePhoneNumberReturn = {
  phoneNumber: string;
  externalId: string;
};

export type PurchasePhoneNumberFn = (input: PurchasePhoneNumberInput) => Promise<PurchasePhoneNumberReturn>;

type DeactivatePhoneNumberInput = {
  externalId: string;
};

type DeactivatePhoneNumberReturn = void;

export type DeactivatePhoneNumberFn = (input: DeactivatePhoneNumberInput) => Promise<DeactivatePhoneNumberReturn>;

type PlayVoicePromptInput = {
  prompt: VoicePrompt;
  language: PreferredLanguage;
  initialPauseLength?: number;
};

export type PlayVoicePromptReturn = {
  xml?: string;
};

export type PlayVoicePromptFn = (input: PlayVoicePromptInput) => Promise<PlayVoicePromptReturn> | PlayVoicePromptReturn;

type DeleteRecordingInput = {
  recordingId: string;
};

export type DeleteRecordingFn = (input: DeleteRecordingInput) => Promise<void>;

type GetCallReturn = ParsePhoneCallReceivedWebhookResultReturn;

type GetCallFn = (callSid: string) => Promise<GetCallReturn>;

export type HangupReturn = {
  xml?: string;
};

export type HangupFn = () => HangupReturn;

export interface PhoneProviderPackage {
  sendSms: SendSmsFn;
  getPhoneNumberInfo: GetPhoneNumberInfoFn;
  listActiveNumbers: ListActiveNumbersFn;
  purchasePhoneNumber: PurchasePhoneNumberFn;
  deactivatePhoneNumber: DeactivatePhoneNumberFn;
  forwardPhoneCall: ForwardPhoneCallFn;
  parseSmsReceivedWebhook: ParseSmsReceivedWebhookFn;
  parseSmsStatusUpdate: ParseSmsStatusUpdateFn;
  parsePhoneCallReceivedWebhook: ParsePhoneCallReceivedWebhookFn;
  receivePhoneCallRecording: ReceivePhoneCallRecordingFn;
  receivePhoneCallSummary: ReceivePhoneCallSummaryFn;
  receivePhoneCallTranscript: ReceivePhoneCallTranscriptFn;
  playVoicePrompt: PlayVoicePromptFn;
  deleteRecording: DeleteRecordingFn;
  getCall: GetCallFn;
  hangup: HangupFn;
}
